import { useEffect, useRef, useState } from 'react'
import type { InteractionProps } from './registry'
import { getNonEmptyString } from './shared/config'
import { asRecord } from './shared/response'
import controls from './shared/controls.module.css'
import styles from './BankBalanceSheetLab.module.css'
import {
  MIN_CASH,
  applyAction,
  explain,
  formatUsd,
  income,
  isBalanced,
  isComplete,
  liquidity,
  replay,
  totalAssets,
  totalLiabilitiesAndEquity,
  type BankAction,
} from './bankLab/model'

interface Transaction {
  action: BankAction
  label: string
}

const TRANSACTIONS: Transaction[] = [
  { action: 'receive-deposit', label: 'Receive a $1M deposit' },
  { action: 'make-loan', label: 'Make a $1M loan from cash' },
  { action: 'buy-security', label: 'Buy a $1M security with cash' },
]

/**
 * Custom teaching interaction: a deterministic balance-sheet sandbox. The
 * learner runs a few transactions and watches the sheet stay balanced, income
 * shift, and cash (liquidity) fall toward a visible floor when a
 * profitable-looking move consumes it. All math lives in `./bankLab/model`.
 */
export function BankBalanceSheetLab({
  config,
  savedResponse,
  onResponseChange,
  onComplete,
}: InteractionProps) {
  const bankName = getNonEmptyString(config, 'bank') ?? 'the bank'
  const task =
    getNonEmptyString(config, 'task') ??
    `Put at least $1M of idle cash to work in an earning asset, keeping cash at or above ${formatUsd(
      MIN_CASH,
    )}.`

  const saved = asRecord(savedResponse)
  const [actions, setActions] = useState<BankAction[]>(() =>
    Array.isArray(saved.actions) ? (saved.actions as BankAction[]) : [],
  )
  const [message, setMessage] = useState<string>('')

  const state = replay(actions)
  const { earned, paid, nii } = income(state)
  const cash = liquidity(state)
  const atFloor = cash <= MIN_CASH
  const completed = isComplete(actions)

  const completedRef = useRef(false)
  useEffect(() => {
    if (completed && !completedRef.current) {
      completedRef.current = true
      onComplete?.()
    }
  }, [completed, onComplete])

  function commit(next: BankAction[], note: string) {
    setActions(next)
    setMessage(note)
    onResponseChange?.({ actions: next })
  }

  function runAction(action: BankAction) {
    const result = applyAction(state, action)
    if (!result.ok) {
      setMessage(result.error ?? 'That action is not allowed right now.')
      return
    }
    commit([...actions, action], explain(action))
  }

  function undo() {
    if (actions.length === 0) return
    commit(actions.slice(0, -1), 'Reversed the last transaction.')
  }

  function reset() {
    if (actions.length === 0) return
    commit([], 'Reset to the starting balance sheet.')
  }

  return (
    <section className={styles.lab} aria-label={`Balance-sheet lab for ${bankName}`}>
      <div className={styles.sheet}>
        <div className={styles.column}>
          <h4 className={styles.colHead}>Assets</h4>
          <Line label="Cash & reserves" value={state.cash} highlight={atFloor} />
          <Line label="Securities" value={state.securities} />
          <Line label="Loans" value={state.loans} />
          <Line label="Total" value={totalAssets(state)} total />
        </div>
        <div className={styles.column}>
          <h4 className={styles.colHead}>Liabilities &amp; Equity</h4>
          <Line label="Deposits" value={state.deposits} />
          <Line label="Borrowings" value={state.borrowings} />
          <Line label="Equity" value={state.equity} />
          <Line label="Total" value={totalLiabilitiesAndEquity(state)} total />
        </div>
      </div>

      <p className={styles.balance} role="status" aria-live="polite">
        <span className={styles.mark} aria-hidden="true">
          {isBalanced(state) ? '✓' : '✗'}
        </span>
        {isBalanced(state) ? 'In balance' : 'Out of balance'} — both sides {formatUsd(totalAssets(state))}
      </p>

      <div className={styles.panels}>
        <div className={styles.panel}>
          <h4 className={styles.panelHead}>Period income (annual)</h4>
          <Line label="Interest earned" value={earned} />
          <Line label="Interest paid" value={paid} />
          <Line label="Net interest income" value={nii} total />
        </div>
        <div className={`${styles.panel} ${atFloor ? styles.warn : ''}`}>
          <h4 className={styles.panelHead}>Liquidity</h4>
          <Line label="Cash on hand" value={cash} highlight={atFloor} />
          <Line label="Minimum" value={MIN_CASH} />
          <p className={styles.floorNote}>
            {atFloor
              ? 'Cash is at the minimum — deploy more only after new funding.'
              : 'Cash is above the minimum.'}
          </p>
        </div>
      </div>

      <div className={styles.actions}>
        {TRANSACTIONS.map((txn) => (
          <button
            key={txn.action}
            type="button"
            className={controls.button}
            onClick={() => runAction(txn.action)}
          >
            {txn.label}
          </button>
        ))}
      </div>

      <div className={controls.actions}>
        <button type="button" className={controls.button} onClick={undo} disabled={actions.length === 0}>
          Undo
        </button>
        <button type="button" className={controls.button} onClick={reset} disabled={actions.length === 0}>
          Reset
        </button>
      </div>

      <p className={styles.explain} role="status" aria-live="polite">
        {message}
      </p>

      <p className={`${styles.task} ${completed ? styles.taskDone : ''}`}>
        <span className={styles.mark} aria-hidden="true">
          {completed ? '✓' : '•'}
        </span>
        <span>
          <strong>Task:</strong> {task}
          {completed ? ' Done — you funded an earning asset and kept cash above the floor.' : ''}
        </span>
      </p>
    </section>
  )
}

function Line({
  label,
  value,
  total,
  highlight,
}: {
  label: string
  value: number
  total?: boolean
  highlight?: boolean
}) {
  return (
    <div className={`${styles.line} ${total ? styles.lineTotal : ''} ${highlight ? styles.lineWarn : ''}`}>
      <span>{label}</span>
      <span className={styles.value}>{formatUsd(value)}</span>
    </div>
  )
}

// Fallback for genuinely broken config is unnecessary here: the lab has no
// required config and always renders a valid starting model.
